import { useState } from 'react';
import { AlertCircle, Printer, Bus, Bell } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Badge from '../../components/common/Badge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import { api } from '../../utils/api';

function DueTracking() {
  const { students, feeStructures, feePayments, classes, parents, routes, transportTerms, academicYear } = useApp();
  const [filterClass, setFilterClass] = useState('all');
  const [tab, setTab] = useState('tuition'); // 'tuition' | 'transport'
  const [sending, setSending] = useState(false);

  // ── Tuition due list ──────────────────────────────────────────────────────
  const tuitionDueStudents = students.filter(s => s.status === 'active').map(s => {
    const cls = classes.find(c => c.id === s.classId);
    const className = cls?.name;
    const fs = [...feeStructures]
      .filter(f => (className && f.classId === className) || f.classId === s.classId)
      .filter(f => !academicYear || !f.academicYear || f.academicYear === academicYear)
      .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0] || null;
    if (!fs) return null;
    const prevBal  = Number(s.previousYearBalance || 0);
    const totalDue = Number(fs.totalAmount || 0) + prevBal;
    const paid     = feePayments.filter(p => p.studentId === s.id && p.type !== 'transportFee' && p.type !== 'admissionFee').reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const remaining = totalDue - paid;
    if (remaining <= 0) return null;
    const parent = parents.find(p => p.id === s.parentId);

    // Per-term balance using fee_components isTermFee entries
    const termComps = (fs.components || [])
      .filter(c => c.isTermFee)
      .sort((a, b) => (a.termNo || 0) - (b.termNo || 0));
    let cum = 0;
    const terms = termComps.map(tc => {
      const tAmt  = Number(tc.amount || 0);
      const prevC = cum;
      cum += tAmt;
      const tPaid = Math.min(Math.max(paid - prevC, 0), tAmt);
      const tBal  = tAmt - tPaid;
      const tSt   = paid >= cum ? 'paid' : paid > prevC ? 'partial' : 'pending';
      return { termNo: tc.termNo, amount: tAmt, dueDate: tc.termDueDate || '', termPaid: tPaid, termBal: tBal, tStatus: tSt };
    });

    return {
      ...s,
      feeTotal: totalDue, paid, remaining,
      status: paid > 0 ? 'partial' : 'pending',
      className:   cls    ? `${cls.name}-${cls.section}` : '-',
      dueDate:     fs.dueDate,
      parentPhone: parent?.phone || '—',
      terms,
    };
  }).filter(Boolean);

  // ── Term list (canonical, for header columns) ─────────────────────────────
  const termDefs = transportTerms.length > 0
    ? transportTerms
    : [{ termNo: 1, label: 'Annual', percent: 100, dueDate: '' }];

  // ── Transport due list ────────────────────────────────────────────────────
  const transportDueStudents = students.filter(s => s.status === 'active' && s.transportRouteId).map(s => {
    const route     = routes.find(r => r.id === s.transportRouteId);
    const stop      = (route?.stops || []).find(st => st.name === s.transportStop);
    const annualFee = stop ? Number(stop.fee) : 0;
    if (annualFee <= 0) return null;

    const paid      = feePayments.filter(p => p.studentId === s.id && p.type === 'transportFee').reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const remaining = annualFee - paid;
    if (remaining <= 0) return null;

    // Compute ALL terms (including paid) so each column always has a value
    let cum = 0;
    const terms = termDefs.map(t => {
      const tAmt    = Math.round(annualFee * t.percent / 100 * 100) / 100;
      const prevCum = cum;
      cum += tAmt;
      const tPaid   = Math.min(Math.max(paid - prevCum, 0), tAmt);
      const tBal    = tAmt - tPaid;
      const tStatus = paid >= cum ? 'paid' : paid > prevCum ? 'partial' : 'pending';
      return { ...t, amount: tAmt, termPaid: tPaid, termBal: tBal, tStatus };
    });

    const cls    = classes.find(c => c.id === s.classId);
    const parent = parents.find(p => p.id === s.parentId);
    return {
      ...s,
      routeName: route?.routeName || route?.name || '—',
      stopName:  s.transportStop || stop?.name || '—',
      annualFee, paid, remaining,
      status: paid > 0 ? 'partial' : 'pending',
      terms,
      className:   cls    ? `${cls.name}-${cls.section}` : '-',
      parentPhone: parent?.phone || '—',
    };
  }).filter(Boolean);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const filteredTuition   = filterClass === 'all' ? tuitionDueStudents   : tuitionDueStudents.filter(s => s.classId === filterClass);
  const filteredTransport = filterClass === 'all' ? transportDueStudents : transportDueStudents.filter(s => s.classId === filterClass);

  // Term column definitions for tuition (only terms with non-zero amount)
  const tuitionTermDefs = (() => {
    const map = new Map();
    filteredTuition.forEach(s => {
      (s.terms || []).forEach(t => {
        if (t.amount > 0 && !map.has(t.termNo))
          map.set(t.termNo, { termNo: t.termNo, label: `Term ${t.termNo}`, dueDate: t.dueDate });
      });
    });
    return [...map.values()].sort((a, b) => a.termNo - b.termNo);
  })();

  // Transport term columns — only terms with non-zero percent
  const visibleTransportTerms = termDefs.filter(t => (t.percent || 0) > 0);

  const list       = tab === 'tuition' ? filteredTuition : filteredTransport;
  const totalDue   = list.reduce((sum, s) => sum + s.remaining, 0);
  const totalPaid  = list.reduce((sum, s) => sum + s.paid, 0);
  const totalFee   = list.reduce((sum, s) => sum + (tab === 'tuition' ? s.feeTotal : s.annualFee), 0);

  const selectedClassName = filterClass === 'all' ? 'All Classes' : (() => {
    const c = classes.find(c => c.id === filterClass);
    return c ? `${c.name}-${c.section}` : '';
  })();
  const fmt = n => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fmtDueDate = d => {
    if (!d) return '';
    const p = String(d).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const printTuitionReport = () => {
    const termHeaders = tuitionTermDefs.map(t =>
      `<th class="num">${t.label} Due${t.dueDate ? `<br><span style="font-weight:400;font-size:10px;color:#666">${fmtDueDate(t.dueDate)}</span>` : ''}</th>`
    ).join('');
    const termTotals = tuitionTermDefs.map(td =>
      `<td class="num">${fmt(filteredTuition.reduce((s, r) => s + ((r.terms || []).find(x => x.termNo === td.termNo)?.termBal || 0), 0))}</td>`
    ).join('');

    const rows = filteredTuition.map((s, i) => {
      const termCells = tuitionTermDefs.map(td => {
        const t = (s.terms || []).find(x => x.termNo === td.termNo);
        if (!t) return `<td class="num" style="color:#ccc">—</td>`;
        return t.tStatus === 'paid'
          ? `<td class="num" style="color:#16a34a">✓ Paid</td>`
          : `<td class="num" style="color:#dc2626;font-weight:600">${fmt(t.termBal)}</td>`;
      }).join('');
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${s.firstName} ${s.lastName}</td>
          <td>${s.parentPhone}</td>
          <td>${s.className}</td>
          <td class="num">${fmt(s.feeTotal)}</td>
          <td class="num" style="color:#16a34a">${fmt(s.paid)}</td>
          ${termCells}
          <td class="num" style="color:#dc2626;font-weight:600">${fmt(s.remaining)}</td>
          <td><span class="badge badge-${s.status}">${s.status.charAt(0).toUpperCase() + s.status.slice(1)}</span></td>
        </tr>
      `;
    }).join('');

    const tfoot = tuitionTermDefs.length > 0 ? `
      <tfoot><tr class="total-row">
        <td colspan="5">Grand Total</td>
        <td class="num" style="color:#16a34a">${fmt(filteredTuition.reduce((s, r) => s + r.paid, 0))}</td>
        ${termTotals}
        <td class="num" style="color:#dc2626">${fmt(totalDue)}</td>
        <td></td>
      </tr></tfoot>
    ` : '';

    openPrint('Tuition Due Tracking', selectedClassName, filteredTuition.length, fmt(totalFee), fmt(totalPaid), fmt(totalDue), `
      <thead><tr>
        <th>#</th><th>Student</th><th>Parent Phone</th><th>Class</th>
        <th class="num">Total Fee (₹)</th><th class="num">Paid (₹)</th>
        ${termHeaders}
        <th class="num">Remaining (₹)</th><th>Status</th>
      </tr></thead><tbody>${rows}</tbody>${tfoot}
    `, 6 + tuitionTermDefs.length);
  };

  const printTransportReport = () => {
    const termHeaders = visibleTransportTerms.map(t =>
      `<th class="num">${t.label} Due${t.dueDate ? `<br><span style="font-weight:400;font-size:10px;color:#666">${fmtDueDate(t.dueDate)}</span>` : ''}</th>`
    ).join('');
    const termTotals  = visibleTransportTerms.map((t, ti) => {
      const total = filteredTransport.reduce((sum, s) => sum + (s.terms[ti]?.termBal || 0), 0);
      return `<td class="num" style="color:#dc2626">${fmt(total)}</td>`;
    }).join('');

    const rows = filteredTransport.map((s, i) => {
      const termCells = s.terms.map(t =>
        t.tStatus === 'paid'
          ? `<td class="num" style="color:#16a34a">✓ Paid</td>`
          : `<td class="num" style="color:#dc2626;font-weight:600">${fmt(t.termBal)}</td>`
      ).join('');
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${s.firstName} ${s.lastName}</td>
          <td>${s.parentPhone}</td>
          <td>${s.className}</td>
          <td>${s.routeName} / ${s.stopName}</td>
          <td class="num">${fmt(s.annualFee)}</td>
          <td class="num" style="color:#16a34a">${fmt(s.paid)}</td>
          ${termCells}
          <td class="num" style="color:#dc2626;font-weight:600">${fmt(s.remaining)}</td>
          <td><span class="badge badge-${s.status}">${s.status.charAt(0).toUpperCase() + s.status.slice(1)}</span></td>
        </tr>
      `;
    }).join('');

    openPrint('Transport Due Tracking', selectedClassName, filteredTransport.length, fmt(totalFee), fmt(totalPaid), fmt(totalDue), `
      <thead><tr>
        <th>#</th><th>Student</th><th>Phone</th><th>Class</th><th>Route / Stop</th>
        <th class="num">Annual Fee</th><th class="num">Paid</th>
        ${termHeaders}
        <th class="num">Total Due</th><th>Status</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="6">Grand Total</td>
          <td class="num" style="color:#16a34a">${fmt(totalPaid)}</td>
          ${termTotals}
          <td class="num" style="color:#dc2626">${fmt(totalDue)}</td>
          <td></td>
        </tr>
      </tfoot>
    `, 6 + visibleTransportTerms.length);
  };

  const openPrint = async (title, className, count, fee, paid, due, tableBody, fixedCols) => {
    const school = await getSchoolInfo();
    const win = window.open('', '_blank', 'width=1100,height=700');
    win.document.open(); win.document.write(`
      <html><head><title>${title} Report</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Roboto',Arial,sans-serif; padding:28px 32px; color:#111; font-size:13px; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .title { font-size:15px; font-weight:700; color:#6b21a8; }
        .meta-left .sub { font-size:11px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:11px; color:#555; }
        .summary { display:flex; gap:12px; margin-bottom:16px; }
        .summary-box { flex:1; padding:8px 12px; border-radius:8px; border:1px solid #e5e7eb; }
        .summary-box .lbl { font-size:10px; color:#888; margin-bottom:2px; }
        .summary-box .val { font-size:14px; font-weight:700; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 9px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:6px 9px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:11px; }
        tfoot td { background:#f1f5f9; font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; padding:7px 9px; font-size:11px; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .badge { display:inline-block; padding:1px 8px; border-radius:20px; font-size:10px; font-weight:600; }
        .badge-partial { background:#fef3c7; color:#92400e; }
        .badge-pending { background:#fee2e2; color:#991b1b; }
        .footer { text-align:center; font-size:10px; color:#aaa; margin-top:20px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; margin-top:16px; }
        @page { margin:3mm; size:A4 landscape; }
        @media print { .print-btn{display:none} body{padding:4px} *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important} img{display:block!important} }
      </style></head><body>
      ${schoolHeader(school, '#6b21a8', `${title} Report — Academic Year ${academicYear}`)}
      <div class="meta">
        <div class="meta-left">
          <div class="title">${className}</div>
          <div class="sub">${count} student${count !== 1 ? 's' : ''} with pending fees</div>
        </div>
        <div class="meta-right">Date: <strong>${formatDate(new Date().toISOString().split('T')[0])}</strong></div>
      </div>
      <div class="summary">
        <div class="summary-box"><div class="lbl">Total Fee</div><div class="val">${fee}</div></div>
        <div class="summary-box"><div class="lbl">Total Paid</div><div class="val" style="color:#16a34a">${paid}</div></div>
        <div class="summary-box"><div class="lbl">Total Due</div><div class="val" style="color:#dc2626">${due}</div></div>
        <div class="summary-box"><div class="lbl">Collection Rate</div><div class="val">${totalFee > 0 ? Math.round((totalPaid/totalFee)*100) : 0}%</div></div>
      </div>
      <table>${tableBody}</table>
      <div class="footer">Computer-generated report — ${school.name}</div>
      <div class="print-btn">
        <button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:9px 26px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',Arial,sans-serif">🖨 Print</button>
      </div>
      </body></html>
    `); win.document.close();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold">Due Tracking</h1>
          <p className="mt-0.5 text-gray-500 text-sm">
            {tab === 'tuition'
              ? `${filteredTuition.length} students with pending tuition fees`
              : `${filteredTransport.length} students with pending transport fees`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={async () => {
              setSending(true);
              try {
                await api.create('fee-payments/send-due-reminders', { daysBeforeDue: 3 });
                alert('✅ Fee reminders are being sent. Push notifications and WhatsApp messages will be delivered shortly.');
              } catch (e) { alert('Error: ' + e.message); }
              setSending(false);
            }}
            disabled={sending}
            className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-60">
            <Bell size={14} /> {sending ? 'Sending…' : 'Send Due Reminders'}
          </button>
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            <option value="all">All Classes</option>
            {(() => {
              const names = [...new Set(classes.map(c => c.name))].sort((a, b) => {
                const nA = parseInt(a.replace(/\D/g,'')) || 0, nB = parseInt(b.replace(/\D/g,'')) || 0;
                return nA !== nB ? nA - nB : a.localeCompare(b);
              });
              return names.map(name => {
                const secs = classes.filter(c => c.name === name).sort((a, b) => a.section.localeCompare(b.section));
                return <optgroup key={name} label={name}>
                  {secs.map(c => <option key={c.id} value={c.id}>Section {c.section}</option>)}
                </optgroup>;
              });
            })()}
          </select>
          <button onClick={tab === 'tuition' ? printTuitionReport : printTransportReport}
            className="flex items-center gap-1.5 px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            <Printer size={14} /> Print
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-1 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" />
            <span className="font-semibold text-red-700">Total Due: {formatCurrency(totalDue)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('tuition')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'tuition' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
          Tuition Fees
          <span className={`px-1.5 py-0.5 rounded-full text-xs ${tab === 'tuition' ? 'bg-red-100 text-red-600' : 'bg-gray-200 text-gray-500'}`}>{filteredTuition.length}</span>
        </button>
        <button onClick={() => setTab('transport')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'transport' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
          <Bus size={14} /> Transport Fees
          <span className={`px-1.5 py-0.5 rounded-full text-xs ${tab === 'transport' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>{filteredTransport.length}</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1 text-sm text-gray-500">Total {tab === 'transport' ? 'Annual' : ''} Fee</p>
          <p className="text-lg font-bold">{formatCurrency(totalFee)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1 text-sm" style={{ color: '#16a34a' }}>Total Paid</p>
          <p className="text-lg font-bold" style={{ color: '#16a34a' }}>{formatCurrency(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1 text-sm" style={{ color: '#dc2626' }}>Total Due</p>
          <p className="text-lg font-bold" style={{ color: '#dc2626' }}>{formatCurrency(totalDue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1 text-sm text-gray-500">Collection Rate</p>
          <p className="text-lg font-bold">{totalFee > 0 ? Math.round((totalPaid / totalFee) * 100) : 0}%</p>
        </div>
      </div>

      {/* ── Tuition tab ── */}
      {tab === 'tuition' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-2 text-left font-semibold">#</th>
                <th className="px-4 py-2 text-left font-semibold">Student</th>
                <th className="px-4 py-2 text-left font-semibold">Class</th>
                <th className="px-4 py-2 text-left font-semibold">Parent Phone</th>
                <th className="px-4 py-2 text-right font-semibold">Total Fee</th>
                <th className="px-4 py-2 text-right font-semibold">Paid</th>
                {tuitionTermDefs.map(t => (
                  <th key={t.termNo} className="px-4 py-2 text-right font-semibold">
                    <div>{t.label} Due</div>
                    {t.dueDate && <div className="text-xs font-normal text-gray-400">{fmtDueDate(t.dueDate)}</div>}
                  </th>
                ))}
                <th className="px-4 py-2 text-right font-semibold">Total Remaining</th>
                <th className="px-4 py-2 text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredTuition.map((s, i) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-1.5 text-gray-400 text-xs">{i + 1}</td>
                  <td className="px-4 py-1.5 font-medium">{s.firstName} {s.lastName}</td>
                  <td className="px-4 py-1.5">{s.className}</td>
                  <td className="px-4 py-1.5 text-gray-500">{s.parentPhone}</td>
                  <td className="px-4 py-1.5 text-right">{formatCurrency(s.feeTotal)}</td>
                  <td className="px-4 py-1.5 text-right text-green-600 font-medium">{formatCurrency(s.paid)}</td>
                  {tuitionTermDefs.map(td => {
                    const t = (s.terms || []).find(x => x.termNo === td.termNo);
                    if (!t) return <td key={td.termNo} className="px-4 py-1.5 text-right text-gray-300">—</td>;
                    return (
                      <td key={td.termNo} className="px-4 py-1.5 text-right">
                        {t.tStatus === 'paid' ? (
                          <span className="text-green-500 text-xs font-semibold">✓ Paid</span>
                        ) : (
                          <span className={`font-semibold ${t.tStatus === 'partial' ? 'text-amber-600' : 'text-gray-700'}`}>
                            {formatCurrency(t.termBal)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-1.5 text-right font-bold text-gray-900">{formatCurrency(s.remaining)}</td>
                  <td className="px-4 py-1.5 text-center"><Badge value={s.status} /></td>
                </tr>
              ))}
              {filteredTuition.length === 0 && (
                <tr><td colSpan={8 + tuitionTermDefs.length} className="py-8 text-center text-gray-400">All tuition fees are cleared!</td></tr>
              )}
            </tbody>
            {filteredTuition.length > 0 && tuitionTermDefs.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-sm">
                  <td colSpan="5" className="px-4 py-2 text-gray-600">Total</td>
                  <td className="px-4 py-2 text-right">
                    {formatCurrency(filteredTuition.reduce((s, r) => s + r.paid, 0))}
                  </td>
                  {tuitionTermDefs.map(td => (
                    <td key={td.termNo} className="px-4 py-2 text-right">
                      {formatCurrency(filteredTuition.reduce((s, r) => s + ((r.terms || []).find(x => x.termNo === td.termNo)?.termBal || 0), 0))}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right">
                    {formatCurrency(filteredTuition.reduce((s, r) => s + r.remaining, 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Transport tab ── */}
      {tab === 'transport' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-50 border-b border-blue-100">
                <th className="px-3 py-2 text-left font-semibold">#</th>
                <th className="px-3 py-2 text-left font-semibold">Student</th>
                <th className="px-3 py-2 text-left font-semibold">Class</th>
                <th className="px-3 py-2 text-left font-semibold">Route / Stop</th>
                <th className="px-3 py-2 text-left font-semibold">Parent Phone</th>
                <th className="px-3 py-2 text-right font-semibold">Annual Fee</th>
                <th className="px-3 py-2 text-right font-semibold">Paid</th>
                {visibleTransportTerms.map(t => (
                  <th key={t.termNo} className="px-3 py-2 text-right font-semibold">
                    <div>{t.label} Due</div>
                    {t.dueDate && <div className="text-xs font-normal text-gray-400">{fmtDueDate(t.dueDate)}</div>}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold">Total Due</th>
                <th className="px-3 py-2 text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredTransport.map((s, i) => (
                <tr key={s.id} className="hover:bg-blue-50/30">
                  <td className="px-3 py-1.5 text-gray-400 text-xs">{i + 1}</td>
                  <td className="px-3 py-1.5 font-medium">{s.firstName} {s.lastName}</td>
                  <td className="px-3 py-1.5">{s.className}</td>
                  <td className="px-3 py-1.5">
                    <span className="font-medium">{s.routeName}</span>
                    <span className="text-gray-400 text-xs ml-1">/ {s.stopName}</span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">{s.parentPhone}</td>
                  <td className="px-3 py-1.5 text-right">{formatCurrency(s.annualFee)}</td>
                  <td className="px-3 py-1.5 text-right text-green-600 font-medium">{formatCurrency(s.paid)}</td>
                  {visibleTransportTerms.map((td, ti) => {
                    const t = s.terms[ti];
                    if (!t) return null;
                    return (
                      <td key={td.termNo} className="px-3 py-1.5 text-right">
                        {t.tStatus === 'paid' ? (
                          <span className="text-green-500 text-xs font-semibold">✓ Paid</span>
                        ) : (
                          <span className={`font-semibold ${t.tStatus === 'partial' ? 'text-amber-600' : 'text-gray-700'}`}>
                            {formatCurrency(t.termBal)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-right font-bold text-gray-900">{formatCurrency(s.remaining)}</td>
                  <td className="px-3 py-1.5 text-center"><Badge value={s.status} /></td>
                </tr>
              ))}
              {filteredTransport.length === 0 && (
                <tr>
                  <td colSpan={7 + visibleTransportTerms.length + 2} className="py-8 text-center text-gray-400">
                    All transport fees are cleared!
                  </td>
                </tr>
              )}
            </tbody>
            {/* Term column totals */}
            {filteredTransport.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200 font-semibold text-sm">
                  <td colSpan="6" className="px-3 py-2 text-gray-600">Total</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrency(filteredTransport.reduce((s, r) => s + r.paid, 0))}
                  </td>
                  {visibleTransportTerms.map((t, ti) => (
                    <td key={t.termNo} className="px-3 py-2 text-right">
                      {formatCurrency(filteredTransport.reduce((s, r) => s + (r.terms[ti]?.termBal || 0), 0))}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    {formatCurrency(filteredTransport.reduce((s, r) => s + r.remaining, 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
export default DueTracking;
